// src/pages/client-management/components/ClientProfileDrawer.jsx
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import Button from "../../../components/ui/Button";
import { useNavigate } from "react-router-dom";
import { getClient } from "../../../lib/clients";

const ClientProfileDrawer = ({
    client,
    isOpen,
    onClose,
    onBookAppointment,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [detail, setDetail] = useState(null);

    useEffect(() => {
        if (!isOpen || !client?.id) { setDetail(null); return; }
        getClient(client.id).then(setDetail).catch(() => {});
    }, [isOpen, client?.id]);

    if (!isOpen || !client) return null;
    const c = detail || client;

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const d = new Date(dateString);
        if (Number.isNaN(d.getTime())) return "-";
        return d.toLocaleDateString("ro-RO", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("ro-RO", {
            style: "currency",
            currency: "RON",
        }).format(amount || 0);
    };

    const statusLabel =
        c?.status === "active"
            ? t("clients.statusActive")
            : c?.status === "inactive"
                ? t("clients.statusInactive")
                : c?.status === "vip"
                    ? t("clients.statusVip")
                    : (c?.status || "-");

    const handleBookAppointment = () => {
        if (typeof onBookAppointment === "function") {
            onBookAppointment(c);
            return;
        }
        const clientId = c?.id;
        const qs = clientId ? `?clientId=${encodeURIComponent(clientId)}` : "";
        navigate(`/app/appointment-calendar${qs}`);
    };

    return (
        <div className="fixed inset-x-0 top-16 bottom-0 z-50 flex justify-end pointer-events-none">
            {/* Overlay */}
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm pointer-events-auto"
                onClick={onClose}
                aria-hidden="true"
            />

            {/* Drawer */}
            <aside className="relative h-full w-full max-w-md bg-background border-l border-border shadow-xl overflow-y-auto pointer-events-auto">
                {/* Header */}
                <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-muted flex items-center justify-center overflow-hidden">
                                <Image
                                    src={
                                        c?.avatar ||
                                        `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(c?.name || "")}`
                                    }
                                    alt={c?.name}
                                    className="w-full h-full object-cover"
                                />
                            </div>

                            {c?.status === "vip" && (
                                <div className="absolute -top-1 -right-1 w-5 h-5 bg-accent rounded-full flex items-center justify-center">
                                    <Icon name="Crown" size={12} color="white" />
                                </div>
                            )}
                        </div>
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <h2 className="text-xl font-semibold">{c?.name}</h2>
                                <span className="px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground">
                                    {statusLabel}
                                </span>
                            </div>
                            {c?.phone && (
                                <p className="text-sm text-muted-foreground">
                                    {c.phone}
                                </p>
                            )}
                            {c?.email && (
                                <p className="text-sm text-muted-foreground">
                                    {c.email}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            iconName="Plus"
                            iconPosition="left"
                            onClick={handleBookAppointment}
                        >
                            {t("clients.schedule")}
                        </Button>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                    {/* Info rapide */}
                    <section className="grid grid-cols-2 gap-4">
                        <div className="bg-muted/60 rounded-lg p-3">
                            <div className="text-xs text-muted-foreground mb-1">
                                {t("clients.columnLastVisit")}
                            </div>
                            <div className="text-sm font-medium">
                                {c?.lastVisit ? formatDate(c.lastVisit) : t("clients.never")}
                            </div>
                        </div>
                        <div className="bg-muted/60 rounded-lg p-3">
                            <div className="text-xs text-muted-foreground mb-1">
                                {t("clients.appointmentsLabel")}
                            </div>
                            <div className="text-sm font-medium">
                                {c?.totalAppointments ?? 0}
                            </div>
                        </div>
                        <div className="bg-muted/60 rounded-lg p-3">
                            <div className="text-xs text-muted-foreground mb-1">
                                {t("clients.totalSpent")}
                            </div>
                            <div className="text-sm font-medium">
                                {formatCurrency(c?.totalSpent)}
                            </div>
                        </div>
                        <div className="bg-muted/60 rounded-lg p-3">
                            <div className="text-xs text-muted-foreground mb-1">
                                {t("clients.loyaltyPoints")}
                            </div>
                            <div className="text-sm font-medium">
                                {c?.loyaltyPoints ?? 0}
                            </div>
                        </div>

                        {c?.status === "vip" && (
                            <div className="bg-muted/60 rounded-lg p-3">
                                <div className="text-xs text-muted-foreground mb-1">
                                    {t("clients.vipDiscount")}
                                </div>
                                <div className="text-sm font-medium">
                                    {(() => {
                                        const raw = c?.vipDiscountPercent;
                                        const s = raw === null || raw === undefined ? "" : String(raw).trim();
                                        return s ? `${s}%` : t("clients.noDiscount");
                                    })()}
                                </div>
                            </div>
                        )}
                    </section>

                    {/* Detalii contact */}
                    {(c?.address ||
                        c?.dateOfBirth ||
                        c?.gender) && (
                            <section>
                                <h3 className="text-sm font-semibold mb-2">
                                    {t("clients.contactDetails")}
                                </h3>
                                <div className="space-y-1 text-sm text-muted-foreground">
                                    {c?.address && (
                                        <div className="flex items-start gap-2">
                                            <Icon
                                                name="MapPin"
                                                size={14}
                                                className="mt-0.5 text-muted-foreground"
                                            />
                                            <span>{c.address}</span>
                                        </div>
                                    )}
                                    {c?.dateOfBirth && (
                                        <div className="flex items-start gap-2">
                                            <Icon
                                                name="Gift"
                                                size={14}
                                                className="mt-0.5 text-muted-foreground"
                                            />
                                            <span>
                                                {t("clients.dateOfBirth")} {formatDate(c.dateOfBirth)}
                                            </span>
                                        </div>
                                    )}
                                    {c?.gender && (
                                        <div className="flex items-start gap-2">
                                            <Icon
                                                name="User"
                                                size={14}
                                                className="mt-0.5 text-muted-foreground"
                                            />
                                            <span>{t("clients.gender")} {c.gender}</span>
                                        </div>
                                    )}
                                </div>
                            </section>
                        )}

                    {/* Servicii preferate */}
                    {c?.preferredServices?.length > 0 && (
                        <section>
                            <h3 className="text-sm font-semibold mb-2">
                                {t("clients.preferredServices")}
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {c.preferredServices.map((service, i) => (
                                    <span
                                        key={i}
                                        className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full"
                                    >
                                        {service}
                                    </span>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Note */}
                    {c?.notes && (
                        <section>
                            <h3 className="text-sm font-semibold mb-2">{t("common.notes")}</h3>
                            <p className="text-sm text-muted-foreground whitespace-pre-line">
                                {c.notes}
                            </p>
                        </section>
                    )}

                    {/* Timeline programări */}
                    <section>
                        <h3 className="text-sm font-semibold mb-2">
                            {t("clients.appointmentHistory")}
                        </h3>
                        {c?.recentAppointments?.length ? (
                            <ol className="space-y-3 border-l border-border pl-4">
                                {c.recentAppointments.map((appt) => (
                                    <li key={appt.id} className="relative">
                                        <span className="absolute -left-[9px] top-1 w-2 h-2 rounded-full bg-primary" />
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Icon
                                                    name="Scissors"
                                                    size={14}
                                                    className="text-muted-foreground"
                                                />
                                                <span className="text-sm">
                                                    {appt.service || t("calendar.service")}
                                                </span>
                                            </div>
                                            <span className="text-xs text-muted-foreground">
                                                {formatDate(appt.date)}
                                            </span>
                                        </div>
                                        <div className="mt-1">
                                            <span
                                                className={`inline-flex px-2 py-0.5 rounded-full text-[11px] ${appt.status === "completed"
                                                    ? "bg-success/10 text-success"
                                                    : appt.status === "cancelled"
                                                        ? "bg-error/10 text-error"
                                                        : "bg-warning/10 text-warning"
                                                    }`}
                                            >
                                                {appt.status === "completed"
                                                    ? t("calendar.statusCompleted")
                                                    : appt.status === "cancelled"
                                                        ? t("calendar.statusCancelled")
                                                        : t("clients.statusScheduled")}
                                            </span>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        ) : (
                            <p className="text-sm text-muted-foreground">
                                {t("clients.noHistory")}
                            </p>
                        )}
                    </section>
                </div>
            </aside>
        </div>
    );
};

export default ClientProfileDrawer;
